import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Clock, MapPin, Phone, Package, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { API, useCart } from "@/App";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const { clearCart } = useCart();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);

  // Poll payment status if coming from Stripe
  useEffect(() => {
    if (sessionId && pollingCount < 5) {
      const checkPayment = async () => {
        try {
          const res = await axios.get(`${API}/payments/status/${sessionId}`);
          setPaymentStatus(res.data);
          
          if (res.data.payment_status === "paid") {
            clearCart();
          } else if (res.data.status !== "expired") {
            // Continue polling
            setTimeout(() => setPollingCount((c) => c + 1), 2000);
          }
        } catch (error) {
          console.error("[OrderConfirmation] Payment status poll failed:", error);
        }
      };
      checkPayment();
    }
  }, [sessionId, pollingCount, clearCart]);

  // Fetch order details
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        // Get order ID from URL or from payment metadata
        let fetchOrderId = orderId;
        
        if (!fetchOrderId && paymentStatus?.metadata?.order_id) {
          fetchOrderId = paymentStatus.metadata.order_id;
        }

        if (fetchOrderId) {
          const res = await axios.get(`${API}/orders/${fetchOrderId}`);
          setOrder(res.data);
        }
      } catch (error) {
        console.error("[OrderConfirmation] Failed to fetch order:", error);
      } finally {
        setLoading(false);
      }
    };

    // Wait for payment status if from Stripe
    if (sessionId) {
      if (paymentStatus) {
        fetchOrder();
      }
    } else if (orderId) {
      fetchOrder();
    } else {
      setLoading(false);
    }
  }, [orderId, sessionId, paymentStatus]);

  const isPaid = order?.payment_status === "paid" || order?.payment_method === "pay_at_pickup";
  const isProcessing = sessionId && !paymentStatus?.payment_status;

  if (loading || isProcessing) {
    return (
      <div className="min-h-screen bg-white" data-testid="order-confirmation-page">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Loader2 className="h-12 w-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <h1 className="text-xl font-medium text-slate-900">Processing your order...</h1>
          <p className="text-slate-500 mt-2">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-white" data-testid="order-confirmation-page">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-medium text-slate-900">Order not found</h1>
          <p className="text-slate-500 mt-2 mb-6">We couldn't find your order details</p>
          <Link to="/">
            <Button className="bg-emerald-600 hover:bg-emerald-700">Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="order-confirmation-page">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            {isPaid ? "Order Confirmed!" : "Payment Pending"}
          </h1>
          <p className="text-slate-600">
            {isPaid
              ? "Thank you for your order. We'll have it ready for pickup."
              : "Your order will be confirmed once payment is complete."}
          </p>
        </div>

        {/* Order details card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Order header */}
          <div className="p-6 bg-slate-50 border-b">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <p className="text-sm text-slate-500">Order Number</p>
                <p className="font-mono font-semibold text-lg" data-testid="order-id">
                  #{order.id.slice(0, 8).toUpperCase()}
                </p>
              </div>
              <div className="text-right">
                <Badge
                  className={
                    order.order_status === "confirmed"
                      ? "bg-emerald-100 text-emerald-700"
                      : order.order_status === "ready"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-amber-100 text-amber-700"
                  }
                  data-testid="order-status"
                >
                  {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Pickup / Delivery info */}
          <div className="p-6 border-b">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-600" />
              {order.fulfillment_type === "delivery" ? "Delivery Details" : "Pickup Details"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">
                    {order.fulfillment_type === "delivery" ? "Delivery Slot" : "Pickup Time"}
                  </p>
                  <p className="font-medium" data-testid="pickup-time">
                    {new Date(order.pickup_date).toLocaleDateString("en-GB", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}{" "}
                    {order.fulfillment_type === "delivery" ? "between " : "at "}
                    {order.pickup_time}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">
                    {order.fulfillment_type === "delivery" ? "Delivery Address" : "Pickup Location"}
                  </p>
                  {order.fulfillment_type === "delivery" ? (
                    <>
                      <p className="font-medium">{order.delivery_address_line1}</p>
                      {order.delivery_address_line2 && (
                        <p className="text-sm text-slate-600">{order.delivery_address_line2}</p>
                      )}
                      <p className="text-sm text-slate-600">
                        {order.delivery_city}, {order.delivery_postcode}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium">Gorton Superstore</p>
                      <p className="text-sm text-slate-600">578 Hyde Road, Manchester M18 7EE</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Order items */}
          <div className="p-6 border-b">
            <h3 className="font-semibold text-slate-900 mb-4">Order Items</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={`${item.product_id}-${item.weight_option || 'default'}-${index}`} className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.product_name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <p className="font-medium text-slate-900">{item.product_name}</p>
                      <p className="text-sm text-slate-500">
                        {item.quantity} x £{item.price.toFixed(2)}
                        {item.weight_option && ` (${item.weight_option})`}
                      </p>
                    </div>
                  </div>
                  <p className="font-medium">£{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span>£{order.subtotal.toFixed(2)}</span>
              </div>
              {order.fulfillment_type === "delivery" ? (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Delivery</span>
                  <span>£{(order.delivery_fee || 0).toFixed(2)}</span>
                </div>
              ) : (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Collection</span>
                  <span className="text-emerald-600">Free</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold pt-2">
                <span>Total</span>
                <span data-testid="order-total">£{order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment info */}
          <div className="p-6 bg-slate-50">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-slate-500">Payment Method</p>
                <p className="font-medium">
                  {order.payment_method === "pay_at_pickup" && "Pay at Pickup"}
                  {order.payment_method === "bank_transfer" && "Bank Transfer"}
                  {order.payment_method === "stripe" && "Card Payment"}
                </p>
              </div>
              <Badge
                variant={isPaid ? "default" : "secondary"}
                className={isPaid ? "bg-emerald-600" : ""}
              >
                {isPaid ? "Paid" : "Pending"}
              </Badge>
            </div>
          </div>

          {/* Bank transfer details (only for bank_transfer orders) */}
          {order.payment_method === "bank_transfer" && (
            <div className="p-6 border-t border-amber-200 bg-amber-50" data-testid="bank-transfer-instructions">
              <h3 className="font-semibold text-amber-900 mb-3">Pay by Bank Transfer</h3>
              <p className="text-sm text-amber-800 mb-4">
                Please transfer <strong>£{order.total.toFixed(2)}</strong> to the account below.
                Your order will be confirmed as soon as the payment is received.
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm bg-white border border-amber-200 rounded-lg p-4">
                <div>
                  <dt className="text-slate-500">Account Name</dt>
                  <dd className="font-medium text-slate-900">C O International LTD</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Sort Code</dt>
                  <dd className="font-medium text-slate-900 tabular-nums">23-11-85</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Account Number</dt>
                  <dd className="font-medium text-slate-900 tabular-nums">00165856</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Reference</dt>
                  <dd className="font-medium text-slate-900 tabular-nums" data-testid="bank-reference">
                    GS-{order.id.slice(0, 8).toUpperCase()}
                  </dd>
                </div>
              </dl>
              <p className="text-xs text-amber-800 mt-3">
                Please use the reference exactly as shown so we can match your payment to this order.
              </p>
            </div>
          )}
        </div>

        {/* Help section */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 mb-4">Need help with your order?</p>
          <a
            href="tel:07404133860"
            className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700"
          >
            <Phone className="h-4 w-4" />
            Call us: 07404 133860
          </a>
        </div>

        {/* Continue shopping */}
        <div className="mt-8 text-center">
          <Link to="/products">
            <Button variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
